import { FC, useState, useEffect, useRef, useContext } from 'react';
import { IconPlus } from '@tabler/icons-react';
import { Box, Flex, Button, Tooltip } from '@mantine/core';

import { IUserCreate } from 'interfaces/Agent';
import { UpgradePopover } from 'components/upgrade';
import { MAX_FREE_STAFF_MEMBERS, MAX_PRO_STAFF_MEMBERS } from 'constants/settings';
import BillingContext from 'context/BillingContext';
import AuthContext from 'context/AuthContext';
import { AccessLevel } from 'interfaces/GetUserResult';
import StaffListItem from './StaffListitem';

interface IStaffListProps {
  users: IUserCreate[];
  userId: string | null;
  setUserId: (id: string) => void;
  onAddStaff: () => void;
}

const StaffList: FC<IStaffListProps> = ({
  users,
  userId,
  setUserId,
  onAddStaff,
}) => {
  const deleteList: Record<string, boolean> = {};
  const [showUpgradePopover, setShowUpgradePopover] = useState(false);
  const addStaffButtonRef = useRef<HTMLButtonElement>(null);
  const {
    actions: { setPricingOpened },
  } = useContext(BillingContext);
  const {
    data: { account },
  } = useContext(AuthContext);

  const isPro = account?.access_level === AccessLevel.PRO;
  const maxStaffLimit = isPro ? MAX_PRO_STAFF_MEMBERS : MAX_FREE_STAFF_MEMBERS;
  const hasReachedMaxStaff = users.length >= maxStaffLimit;
  const hasReachedAbsoluteLimit = users.length >= MAX_PRO_STAFF_MEMBERS;

  // Add click outside handler
  useEffect(() => {
    if (!showUpgradePopover || !hasReachedMaxStaff) return;
    
    function handleClickOutside(event: MouseEvent) {
      if (addStaffButtonRef.current && 
          !addStaffButtonRef.current.contains(event.target as Node) && 
          !document.querySelector('.mantine-Popover-dropdown')?.contains(event.target as Node)) {
        setShowUpgradePopover(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUpgradePopover, hasReachedMaxStaff]);

  const handleSetDeleteList = (id: string, checked: boolean) => {
    // Implementation of handleSetDeleteList
  };

  const handleUpgrade = () => {
    // Close popover
    setShowUpgradePopover(false);
    
    // Open pricing modal using BillingContext
    setPricingOpened(true);
    
    // Track upgrade button click
    if (typeof window !== 'undefined' && (window as any).analytics && typeof (window as any).analytics.track === 'function') {
      (window as any).analytics.track('staff_limit_upgrade_button_clicked');
    }
  };

  const handleAddStaffClick = () => {
    if (hasReachedAbsoluteLimit) {
      return; // Do nothing if we've reached the absolute limit
    }
    
    if (hasReachedMaxStaff && !isPro) {
      setShowUpgradePopover(true);
      
      // Track upgrade popover shown
      if (typeof window !== 'undefined' && (window as any).analytics && typeof (window as any).analytics.track === 'function') {
        (window as any).analytics.track('staff_limit_popover_shown');
      }
    } else {
      onAddStaff();
    }
  };

  const addStaffButton = (
    <Button
      ref={addStaffButtonRef}
      size="xs"
      variant="default"
      onClick={handleAddStaffClick}
      leftSection={<IconPlus size={14} />}
      disabled={hasReachedAbsoluteLimit}
    >
      Add Staff
    </Button>
  );

  return (
    <>
      <Flex direction="column" gap={0}>
        <Box
          pt="lg"
          bg="white"
          pos="sticky"
          top={0}
          style={{
            zIndex: 10,
            borderTopLeftRadius: 'var(--mantine-radius-md)',
            borderTopRightRadius: 'var(--mantine-radius-md)',
          }}
        >
          <Flex w="100%" px="md" align="center" justify="space-between">
            <h3 className="text-base custom-text-color font-semibold">
              Staff Directory
            </h3>
            {hasReachedAbsoluteLimit ? (
              <Tooltip label="Maximum staff limit reached">
                {addStaffButton}
              </Tooltip>
            ) : !isPro && hasReachedMaxStaff ? (
              <UpgradePopover
                opened={showUpgradePopover}
                onClose={() => setShowUpgradePopover(false)}
                upgradeTitle="Look at you grow!"
                upgradeDescription="To add more team members and unlock more benefits..."
                onUpgrade={handleUpgrade}
                position="bottom"
                closeOnClickOutside={true}
              >
                {addStaffButton}
              </UpgradePopover>
            ) : (
              addStaffButton
            )}
          </Flex>
          <Flex mt="lg">
            {/*{deleteCount > 0 && (
              <Flex
                style={{ marginBottom: '-1px' }}
                align="center"
                justify="space-between"
                className={classes.deleteBanner}
              >
                <Text fz="sm">Delete selected</Text>
                <Button
                  size="xs"
                  onClick={() => setShowConfirmDelete(true)}
                  color="var(--mantine-color-red-6)"
                >
                  Delete {deleteCount}
                </Button>
              </Flex>
            )}*/}
          </Flex>
        </Box>
        <Box>
          {users.map((user, index) => (
            <StaffListItem
              key={user.id}
              user={user}
              index={index}
              userId={userId}
              checkList={deleteList}
              setChecked={handleSetDeleteList}
              setUserId={setUserId}
            />
          ))}
        </Box>
      </Flex>
    </>
  );
};

export default StaffList;
